import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Users, Plus, Calendar, UserCheck, Trash2, Loader2, Truck, Paintbrush,
  CheckCircle, Clock, Navigation, Search, X, MapPin, Phone, Mail, Camera,
  PartyPopper, Package, Store, ChevronRight, AlertTriangle, Upload, History,
} from "lucide-react";
import { toast } from "sonner";
import { logger } from "../utils/logger";
import MaterialIssueModal from "./admin/MaterialIssueModal";
import VendorWorkloadPanel from "./VendorWorkloadPanel";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { id: "confirmed", label: "Confirmed", short: "Confirmed", icon: CheckCircle, color: "#10B981" },
  { id: "team_assigned", label: "Team Assigned", short: "Team", icon: Users, color: "#3B82F6" },
  { id: "on_the_way", label: "On the Way", short: "En route", icon: Truck, color: "#F97316" },
  { id: "setup_started", label: "Setup", short: "Setup", icon: Paintbrush, color: "#7C3AED" },
  { id: "final_touches", label: "Completed with Image", short: "Photo", icon: Camera, color: "#E83E8C" },
  { id: "party_ready", label: "Party On", short: "Party On", icon: PartyPopper, color: "#DC283C" },
];
const STEP_INDEX = (id) => STEPS.findIndex((s) => s.id === id);

const fmtDate = (d) => (d ? String(d).slice(0, 10) : "—");
const relDay = (d) => {
  if (!d) return null;
  const t = new Date(String(d).slice(0, 10)); const now = new Date();
  t.setHours(0, 0, 0, 0); now.setHours(0, 0, 0, 0);
  const diff = Math.round((t - now) / 86400000);
  return diff;
};

const OpsManagementTab = ({ auth, authAxios }) => {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [members, setMembers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [view, setView] = useState("queue"); // queue | team
  const [openBooking, setOpenBooking] = useState(null);
  const [openMember, setOpenMember] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [oRes, mRes, bRes, vRes] = await Promise.all([
        authAxios.get(`${API_URL}/api/admin/ops/overview`),
        authAxios.get(`${API_URL}/api/admin/ops-team`),
        authAxios.get(`${API_URL}/api/admin/ops/bookings`),
        authAxios.get(`${API_URL}/api/admin/ops/vendors`),
      ]);
      setOverview(oRes.data);
      setMembers(mRes.data);
      setBookings(bRes.data);
      setVendors(vRes.data);
    } catch (err) { logger.error(err); toast.error("Failed to load OPS data"); }
    setLoading(false);
  }, [authAxios]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Count assigned bookings per member phone
  const assignedCount = useMemo(() => {
    const map = {};
    bookings.forEach((b) => { if (b.assigned_ops_phone) map[b.assigned_ops_phone] = (map[b.assigned_ops_phone] || 0) + 1; });
    return map;
  }, [bookings]);

  const refreshBooking = useCallback(async () => { await fetchAll(); }, [fetchAll]);

  if (loading) return <div className="text-center py-16"><Loader2 className="animate-spin mx-auto text-[#DC283C]" size={32} /></div>;

  const liveOpen = openBooking ? bookings.find((b) => b.id === openBooking.id) || openBooking : null;

  return (
    <div className="space-y-6" data-testid="ops-management-tab">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[#1E1B4B] flex items-center gap-2">
          <Navigation className="text-[#DC283C]" size={22} /> OPS Team
        </h1>
        <p className="text-sm text-[#64748B] mt-1">Assign crews &amp; vendors, track every confirmed party from setup to Party On.</p>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="ops-kpis">
        <Kpi label="Team Members" value={overview?.total_members} icon={Users} tint="#1E1B4B" />
        <Kpi label="Active Members" value={overview?.active_members} icon={UserCheck} tint="#10B981" />
        <Kpi label="Assigned Bookings" value={overview?.total_assigned_bookings} icon={Calendar} tint="#3B82F6" />
        <Kpi label="In Progress" value={overview?.in_progress_bookings} icon={Clock} tint="#F97316" />
        <Kpi label="Completed" value={overview?.completed_bookings} icon={PartyPopper} tint="#DC283C" />
      </div>

      {/* Segmented toggle */}
      <div className="inline-flex rounded-xl bg-white border border-gray-200 p-1 shadow-sm">
        <button onClick={() => setView("queue")} data-testid="ops-view-queue"
          className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${view === "queue" ? "bg-[#DC283C] text-white" : "text-[#64748B] hover:bg-gray-50"}`}>Work Queue</button>
        <button onClick={() => setView("team")} data-testid="ops-view-team"
          className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${view === "team" ? "bg-[#DC283C] text-white" : "text-[#64748B] hover:bg-gray-50"}`}>Team Members</button>
        <button onClick={() => setView("vendors")} data-testid="ops-view-vendors"
          className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${view === "vendors" ? "bg-[#DC283C] text-white" : "text-[#64748B] hover:bg-gray-50"}`}>Vendor Workload</button>
      </div>

      {view === "queue" ? (
        <WorkQueue bookings={bookings} members={members} vendors={vendors} onOpen={setOpenBooking} />
      ) : view === "vendors" ? (
        <VendorWorkloadPanel authAxios={authAxios} />
      ) : (
        <TeamDirectory members={members} assignedCount={assignedCount} authAxios={authAxios}
          onRefresh={fetchAll} onOpenMember={setOpenMember} />
      )}

      {liveOpen && (
        <BookingDrawer booking={liveOpen} members={members} vendors={vendors} authAxios={authAxios}
          onClose={() => setOpenBooking(null)} onChanged={refreshBooking} />
      )}
      {openMember && (
        <MemberDrawer member={openMember} bookings={bookings.filter((b) => b.assigned_ops_phone === openMember.phone)}
          onClose={() => setOpenMember(null)} onOpenBooking={(b) => { setOpenMember(null); setOpenBooking(b); }} />
      )}
    </div>
  );
};

/* ---------------- KPI ---------------- */
const Kpi = ({ label, value, icon: Icon, tint }) => (
  <div className="admin-stat admin-card rounded-2xl p-4" data-testid={`ops-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="flex items-center gap-2 mb-2">
      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${tint}1a` }}>
        <Icon size={16} style={{ color: tint }} />
      </div>
    </div>
    <p className="text-2xl font-bold text-[#1E1B4B]">{value ?? 0}</p>
    <p className="text-xs text-[#64748B] mt-0.5">{label}</p>
  </div>
);

/* ---------------- Status badge ---------------- */
const StatusBadge = ({ step }) => {
  const s = STEPS[Math.max(0, STEP_INDEX(step))];
  return (
    <span className="px-2.5 py-1 rounded-full text-[11px] font-bold text-white whitespace-nowrap" style={{ background: s?.color || "#94a3b8" }}>
      {s?.label || "Confirmed"}
    </span>
  );
};

/* ---------------- Work Queue ---------------- */
const WorkQueue = ({ bookings, members, vendors, onOpen }) => {
  const [q, setQ] = useState("");
  const [fDate, setFDate] = useState("");
  const [fMember, setFMember] = useState("");
  const [fVendor, setFVendor] = useState("");
  const [fStatus, setFStatus] = useState("");

  const filtered = useMemo(() => {
    return bookings.filter((b) => {
      if (q) {
        const hay = `${b.id} ${b.customer_name || ""}`.toLowerCase();
        if (!hay.includes(q.toLowerCase())) return false;
      }
      if (fDate && fmtDate(b.date) !== fDate) return false;
      if (fMember && b.assigned_ops_phone !== fMember) return false;
      if (fVendor && !(b.assigned_vendors || []).some((v) => v.vendor_id === fVendor)) return false;
      if (fStatus && (b.current_step || "confirmed") !== fStatus) return false;
      return true;
    });
  }, [bookings, q, fDate, fMember, fVendor, fStatus]);

  return (
    <div className="admin-card rounded-2xl p-5" data-testid="ops-work-queue">
      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[180px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94a3b8]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search booking ID or customer…"
            className="w-full pl-9 pr-3 py-2 rounded-xl border border-gray-200 text-sm focus:border-[#DC283C] outline-none" data-testid="ops-queue-search" />
        </div>
        <input type="date" value={fDate} onChange={(e) => setFDate(e.target.value)}
          className="px-3 py-2 rounded-xl border border-gray-200 text-sm focus:border-[#DC283C] outline-none" data-testid="ops-filter-date" />
        <select value={fMember} onChange={(e) => setFMember(e.target.value)}
          className="px-3 py-2 rounded-xl border border-gray-200 text-sm focus:border-[#DC283C] outline-none" data-testid="ops-filter-member">
          <option value="">All members</option>
          {members.filter((m) => m.is_active).map((m) => <option key={m.phone} value={m.phone}>{m.name}</option>)}
        </select>
        <select value={fVendor} onChange={(e) => setFVendor(e.target.value)}
          className="px-3 py-2 rounded-xl border border-gray-200 text-sm focus:border-[#DC283C] outline-none" data-testid="ops-filter-vendor">
          <option value="">All vendors</option>
          {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
        <select value={fStatus} onChange={(e) => setFStatus(e.target.value)}
          className="px-3 py-2 rounded-xl border border-gray-200 text-sm focus:border-[#DC283C] outline-none" data-testid="ops-filter-status">
          <option value="">All statuses</option>
          {STEPS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
      </div>

      {filtered.length === 0 ? (
        <p className="text-sm text-[#94a3b8] text-center py-10">No confirmed bookings match your filters.</p>
      ) : (
        <div className="space-y-2">
          {filtered.map((b) => {
            const step = b.current_step || "confirmed";
            const idx = STEP_INDEX(step);
            const diff = relDay(b.date);
            const delayed = diff !== null && diff < 0 && step !== "party_ready";
            const upcoming = diff !== null && diff >= 0 && diff <= 2 && step !== "party_ready";
            return (
              <button key={b.id} onClick={() => onOpen(b)} data-testid={`ops-booking-row-${b.id}`}
                className="w-full text-left flex items-center gap-3 p-3 rounded-xl border border-gray-100 hover:border-[#DC283C]/40 hover:bg-[#FFF7F8] transition-all">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-sm text-[#1E1B4B] truncate">{b.customer_name || "—"}</p>
                    <span className="text-[11px] text-[#94a3b8]">{b.id}</span>
                    {delayed && <span className="admin-badge admin-badge-danger text-[10px]"><AlertTriangle size={10} /> Delayed</span>}
                    {upcoming && !delayed && <span className="admin-badge admin-badge-warning text-[10px]"><Clock size={10} /> {diff === 0 ? "Today" : `In ${diff}d`}</span>}
                  </div>
                  <p className="text-xs text-[#94a3b8] truncate mt-0.5">
                    {fmtDate(b.date)}{b.setup_time ? ` · ${b.setup_time}` : ""} · {b.package || b.occasion || "—"}
                    {b.assigned_ops_name ? ` · 👤 ${b.assigned_ops_name}` : " · Unassigned"}
                    {(b.assigned_vendors || []).length ? ` · 🛠 ${b.assigned_vendors.length} vendor(s)` : ""}
                  </p>
                  {/* mini progress */}
                  <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden mt-2">
                    <div className="h-full rounded-full transition-all" style={{ width: `${((idx + 1) / STEPS.length) * 100}%`, background: STEPS[idx]?.color }} />
                  </div>
                </div>
                <StatusBadge step={step} />
                <ChevronRight size={16} className="text-[#cbd5e1]" />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ---------------- Team Directory ---------------- */
const TeamDirectory = ({ members, assignedCount, authAxios, onRefresh, onOpenMember }) => {
  const [showAdd, setShowAdd] = useState(false);
  const [name, setName] = useState(""); const [phone, setPhone] = useState(""); const [pin, setPin] = useState("");
  const [q, setQ] = useState(""); const [fActive, setFActive] = useState("");
  const [saving, setSaving] = useState(false);

  const addMember = async () => {
    if (!name || !phone || pin.length < 4) { toast.error("Enter name, phone and a 4-digit PIN"); return; }
    setSaving(true);
    try {
      await authAxios.post(`${API_URL}/api/admin/ops-team`, { name, phone, pin });
      setName(""); setPhone(""); setPin(""); setShowAdd(false);
      toast.success("Member added");
      await onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to add"); }
    setSaving(false);
  };
  const removeMember = async (ph) => {
    if (!window.confirm("Deactivate this member?")) return;
    try { await authAxios.delete(`${API_URL}/api/admin/ops-team/${ph}`); toast.success("Member deactivated"); await onRefresh(); }
    catch { toast.error("Failed"); }
  };

  const filtered = useMemo(() => members.filter((m) => {
    if (q && !`${m.name} ${m.phone} ${m.role || ""}`.toLowerCase().includes(q.toLowerCase())) return false;
    if (fActive === "active" && !m.is_active) return false;
    if (fActive === "inactive" && m.is_active) return false;
    return true;
  }), [members, q, fActive]);

  return (
    <div className="admin-card rounded-2xl p-5" data-testid="ops-team-directory">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex gap-2 flex-1 min-w-[200px]">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94a3b8]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search members…"
              className="w-full pl-9 pr-3 py-2 rounded-xl border border-gray-200 text-sm focus:border-[#DC283C] outline-none" data-testid="ops-member-search" />
          </div>
          <select value={fActive} onChange={(e) => setFActive(e.target.value)}
            className="px-3 py-2 rounded-xl border border-gray-200 text-sm focus:border-[#DC283C] outline-none" data-testid="ops-member-filter">
            <option value="">All</option><option value="active">Active</option><option value="inactive">Inactive</option>
          </select>
        </div>
        <button onClick={() => setShowAdd(!showAdd)} className="px-4 py-2 bg-[#1E1B4B] text-white rounded-xl text-sm font-medium flex items-center gap-2 hover:bg-[#2d2a5e]" data-testid="add-ops-btn">
          <Plus size={14} /> Add Member
        </button>
      </div>

      {showAdd && (
        <div className="flex gap-3 mb-4 p-4 rounded-2xl bg-[#FFF7F8] border border-[#f5d9de] flex-wrap" data-testid="add-ops-form">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" className="flex-1 min-w-[140px] px-3 py-2 rounded-xl border text-sm focus:border-[#DC283C] outline-none" data-testid="ops-name-input" />
          <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone (10 digits)" className="flex-1 min-w-[140px] px-3 py-2 rounded-xl border text-sm focus:border-[#DC283C] outline-none" data-testid="ops-phone-input" />
          <input value={pin} onChange={(e) => setPin(e.target.value)} placeholder="4-digit PIN" maxLength={4} type="password" className="w-28 px-3 py-2 rounded-xl border text-sm focus:border-[#DC283C] outline-none" data-testid="ops-pin-input" />
          <button onClick={addMember} disabled={saving} className="px-4 py-2 bg-[#25D366] text-white rounded-xl text-sm font-medium disabled:opacity-60" data-testid="ops-save-btn">{saving ? "Saving…" : "Save"}</button>
        </div>
      )}

      {filtered.length === 0 ? (
        <p className="text-sm text-[#94a3b8] text-center py-8">No members found.</p>
      ) : (
        <div className="space-y-2">
          {filtered.map((m) => (
            <div key={m.phone} className="flex items-center gap-4 p-3 rounded-xl border border-gray-100 hover:bg-[#FFF7F8]" data-testid={`ops-member-${m.phone}`}>
              <button onClick={() => onOpenMember(m)} className="flex items-center gap-4 flex-1 min-w-0 text-left">
                <div className="w-10 h-10 rounded-full bg-[#1E1B4B] flex items-center justify-center text-white font-bold text-sm">{(m.name || "?")[0].toUpperCase()}</div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-[#1E1B4B] truncate">{m.name}</p>
                  <p className="text-xs text-[#94a3b8]">{m.phone} · {m.role || "executive"}</p>
                </div>
              </button>
              <span className="admin-badge admin-badge-info text-[11px]">{assignedCount[m.phone] || 0} jobs</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${m.is_active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>{m.is_active ? "Active" : "Inactive"}</span>
              {m.is_active && <button onClick={() => removeMember(m.phone)} className="text-red-400 hover:text-red-600" data-testid={`ops-remove-${m.phone}`}><Trash2 size={16} /></button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ---------------- Member Drawer ---------------- */
const MemberDrawer = ({ member, bookings, onClose, onOpenBooking }) => (
  <Drawer title={member.name} subtitle={`${member.phone} · ${member.role || "executive"}`} onClose={onClose} testid="ops-member-drawer">
    <p className="text-xs font-semibold text-[#64748B] uppercase tracking-wide mb-2">Assigned bookings ({bookings.length})</p>
    {bookings.length === 0 ? (
      <p className="text-sm text-[#94a3b8] py-4">No bookings assigned yet.</p>
    ) : (
      <div className="space-y-2">
        {bookings.map((b) => (
          <button key={b.id} onClick={() => onOpenBooking(b)} className="w-full text-left flex items-center gap-2 p-3 rounded-xl border border-gray-100 hover:bg-[#FFF7F8]">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-[#1E1B4B] truncate">{b.customer_name} <span className="text-[11px] text-[#94a3b8]">{b.id}</span></p>
              <p className="text-xs text-[#94a3b8]">{fmtDate(b.date)} · {b.package || b.occasion}</p>
            </div>
            <StatusBadge step={b.current_step || "confirmed"} />
          </button>
        ))}
      </div>
    )}
  </Drawer>
);

/* ---------------- Booking Drawer ---------------- */
const BookingDrawer = ({ booking, members, vendors, authAxios, onClose, onChanged }) => {
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [showVendorPick, setShowVendorPick] = useState(false);
  const [showMaterials, setShowMaterials] = useState(false);
  const fileRef = useRef(null);

  const step = booking.current_step || "confirmed";
  const curIdx = STEP_INDEX(step);
  const assignedVendors = booking.assigned_vendors || [];
  const activeMembers = members.filter((m) => m.is_active);
  const availableVendors = vendors.filter((v) => !assignedVendors.some((a) => a.vendor_id === v.id));

  const loadPhotos = useCallback(async () => {
    try { const r = await authAxios.get(`${API_URL}/api/admin/ops/photos`, { params: { booking_id: booking.id } }); setPhotos(r.data); }
    catch { /* silent */ }
  }, [authAxios, booking.id]);
  useEffect(() => { loadPhotos(); }, [loadPhotos]);

  const hasPhoto = photos.length > 0 || (booking.photo_count || 0) > 0;

  const setStep = async (sid) => {
    if (sid === "party_ready" && !hasPhoto) { toast.error("Upload at least one completion photo first"); return; }
    setBusy(true);
    try {
      await authAxios.post(`${API_URL}/api/admin/ops/update-status`, { booking_id: booking.id, step: sid });
      toast.success(`Status → ${STEPS[STEP_INDEX(sid)].label}`);
      await onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || "Update failed"); }
    setBusy(false);
  };

  const assignOps = async (m) => {
    setBusy(true);
    try {
      await authAxios.put(`${API_URL}/api/admin/ops/assign-member`, { booking_id: booking.id, ops_phone: m.phone, ops_name: m.name });
      toast.success(`Assigned to ${m.name}`); setShowAssign(false); await onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || "Assign failed"); }
    setBusy(false);
  };

  const vendorAction = async (vendor_id, action) => {
    setBusy(true);
    try {
      await authAxios.post(`${API_URL}/api/admin/ops/assign-vendor`, { booking_id: booking.id, vendor_id, action });
      toast.success(action === "remove" ? "Vendor removed" : "Vendor assigned");
      setShowVendorPick(false); await onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || "Action failed"); }
    setBusy(false);
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("photo_type", "completion"); fd.append("booking_id", booking.id);
    setBusy(true);
    try {
      await authAxios.post(`${API_URL}/api/admin/ops/upload-photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo uploaded"); await loadPhotos(); await onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const mapUrl = (booking.location_lat && booking.location_lng)
    ? `https://www.google.com/maps/search/?api=1&query=${booking.location_lat},${booking.location_lng}` : null;

  // Timeline: merge history + step timestamps
  const timeline = useMemo(() => {
    const hist = (booking.status_history || []).map((h) => ({ label: h.label || h.step, by: h.by, at: h.at }));
    if (hist.length) return hist.slice().reverse();
    const ts = booking.step_timestamps || {};
    return STEPS.filter((s) => ts[s.id]).map((s) => ({ label: s.label, by: "", at: ts[s.id] })).reverse();
  }, [booking]);

  return (
    <Drawer title={booking.customer_name || booking.id} subtitle={booking.id} onClose={onClose} testid="ops-booking-drawer"
      headerExtra={<StatusBadge step={step} />}>
      {/* Details grid */}
      <Section title="Booking details">
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <Field icon={Phone} label="Phone" value={booking.customer_phone} />
          <Field icon={Mail} label="Email" value={booking.customer_email} />
          <Field icon={Calendar} label="Date" value={`${fmtDate(booking.date)}${booking.setup_time ? ` · ${booking.setup_time}` : ""}`} />
          <Field icon={Package} label="Package" value={booking.package || booking.occasion} />
          <Field icon={CheckCircle} label="Payment" value={booking.payment_status} />
          <Field icon={Store} label="Occasion" value={booking.occasion} />
        </div>
        <div className="mt-3 flex items-start gap-2 text-sm">
          <MapPin size={15} className="text-[#94a3b8] mt-0.5 shrink-0" />
          <div>
            <p className="text-[#1E1B4B]">{booking.address || "—"}{booking.pincode ? ` · ${booking.pincode}` : ""}</p>
            {mapUrl && <a href={mapUrl} target="_blank" rel="noreferrer" className="text-xs text-[#DC283C] font-semibold inline-flex items-center gap-1 mt-0.5" data-testid="ops-map-link"><Navigation size={11} /> Open in Maps</a>}
          </div>
        </div>
        {booking.notes && (
          <div className="mt-3 p-3 rounded-xl bg-amber-50 border border-amber-100 text-sm text-[#92400E]">
            <span className="font-semibold">Requirements / notes: </span>{booking.notes}
          </div>
        )}
      </Section>

      {/* Assigned OPS */}
      <Section title="Assigned crew">
        <div className="flex items-center gap-2 flex-wrap">
          {booking.assigned_ops_name ? (
            <span className="admin-badge admin-badge-info"><UserCheck size={12} /> {booking.assigned_ops_name}</span>
          ) : <span className="text-sm text-[#94a3b8]">No one assigned yet</span>}
          <button onClick={() => setShowAssign(!showAssign)} className="text-xs font-semibold text-[#DC283C]" data-testid="ops-reassign-btn">{booking.assigned_ops_name ? "Reassign" : "Assign"}</button>
        </div>
        {showAssign && (
          <div className="mt-2 space-y-1 border border-gray-100 rounded-xl p-2">
            {activeMembers.map((m) => (
              <button key={m.phone} onClick={() => assignOps(m)} disabled={busy} className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-[#FFF7F8] text-left text-sm" data-testid={`ops-assign-${m.phone}`}>
                <div className="w-7 h-7 rounded-full bg-[#1E1B4B] flex items-center justify-center text-white text-xs font-bold">{(m.name || "?")[0]}</div>
                <span className="text-[#1E1B4B]">{m.name}</span><span className="text-xs text-[#94a3b8]">{m.phone}</span>
              </button>
            ))}
          </div>
        )}
      </Section>

      {/* Material Issue */}
      <Section title="Material issue & cost">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="text-sm">
            {booking.material_issue_finalized ? (
              <span className="admin-badge admin-badge-success"><Package size={12} /> Finalized · {booking.material_cost_total != null ? `₹${Number(booking.material_cost_total).toLocaleString("en-IN")}` : "locked"}</span>
            ) : (
              <span className="text-[#94a3b8]">No materials recorded yet</span>
            )}
          </div>
          <button onClick={() => setShowMaterials(true)} className="text-xs font-semibold text-[#DC283C]" data-testid="ops-material-issue-btn">
            {booking.material_issue_finalized ? "View materials" : "Record materials"}
          </button>
        </div>
      </Section>
      <Section title="Status workflow">
        <div className="flex gap-1.5 mb-1">
          {STEPS.map((s, si) => {
            const Icon = s.icon; const done = si <= curIdx; const current = s.id === step;
            const gated = s.id === "party_ready" && !hasPhoto;
            const disabled = busy || gated || si > curIdx + 1;
            return (
              <button key={s.id} onClick={() => setStep(s.id)} disabled={disabled} title={gated ? "Upload a completion photo first" : s.label}
                className="flex-1 py-2 rounded-lg border transition-all disabled:cursor-not-allowed"
                style={{ borderColor: current ? s.color : "transparent", background: done ? `${s.color}15` : "#F8FAFC", opacity: disabled && !current && !done ? 0.5 : 1 }}
                data-testid={`ops-step-${booking.id}-${s.id}`}>
                <Icon size={14} className="mx-auto" style={{ color: done ? s.color : "#CBD5E1" }} />
                <p className="text-[10px] font-bold mt-0.5 truncate" style={{ color: done ? s.color : "#CBD5E1" }}>{s.short}</p>
              </button>
            );
          })}
        </div>
        {!hasPhoto && curIdx >= 3 && <p className="text-[11px] text-amber-600 flex items-center gap-1"><AlertTriangle size={11} /> A completion photo is required before "Party On".</p>}
      </Section>

      {/* Vendors */}
      <Section title="Vendors">
        <div className="flex flex-wrap gap-2">
          {assignedVendors.map((v) => (
            <span key={v.vendor_id} className="admin-badge admin-badge-neutral flex items-center gap-1" data-testid={`ops-vendor-chip-${v.vendor_id}`}>
              <Store size={11} /> {v.name} <span className="text-[10px] opacity-70">({v.service_type})</span>
              <button onClick={() => vendorAction(v.vendor_id, "remove")} className="ml-1 text-red-400 hover:text-red-600" aria-label={`Remove vendor ${v.name}`} data-testid={`ops-vendor-remove-${v.vendor_id}`}><X size={11} /></button>
            </span>
          ))}
          <button onClick={() => setShowVendorPick(!showVendorPick)} className="admin-badge admin-badge-info" data-testid="ops-add-vendor-btn"><Plus size={11} /> Add vendor</button>
        </div>
        {showVendorPick && (
          <div className="mt-2 max-h-52 overflow-y-auto border border-gray-100 rounded-xl p-2 space-y-1">
            {availableVendors.length === 0 ? <p className="text-xs text-[#94a3b8] p-2">No more approved vendors available.</p> :
              availableVendors.map((v) => (
                <button key={v.id} onClick={() => vendorAction(v.id, "assign")} disabled={busy} className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-[#FFF7F8] text-left" data-testid={`ops-vendor-pick-${v.id}`}>
                  <Store size={14} className="text-[#64748B]" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[#1E1B4B] truncate">{v.name}</p>
                    <p className="text-[11px] text-[#94a3b8]">{v.service_type} · {v.phone}{v.service_area ? ` · ${v.service_area}` : ""}</p>
                  </div>
                </button>
              ))}
          </div>
        )}
      </Section>

      {/* Completion photos */}
      <Section title="Completion photos">
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid="ops-photo-input" />
        <button onClick={() => fileRef.current?.click()} disabled={busy} className="px-3 py-2 rounded-xl bg-[#1E1B4B] text-white text-sm font-medium flex items-center gap-2 disabled:opacity-60" data-testid="ops-photo-upload-btn">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Upload photo
        </button>
        {photos.length > 0 && (
          <div className="flex gap-2 mt-3 overflow-x-auto">
            {photos.map((p) => (
              <img key={p.id || p.storage_path} src={`${API_URL}/api/files/${p.storage_path}`} alt={p.photo_type} className="w-24 h-24 object-cover rounded-xl border shrink-0" />
            ))}
          </div>
        )}
      </Section>

      {/* Timeline */}
      <Section title={<span className="flex items-center gap-1"><History size={13} /> Activity timeline</span>}>
        {timeline.length === 0 ? <p className="text-sm text-[#94a3b8]">No activity yet.</p> : (
          <div className="space-y-2">
            {timeline.map((t, i) => (
              <div key={`${t.at || ""}-${t.label || ""}-${i}`} className="flex items-start gap-2 text-sm">
                <div className="w-1.5 h-1.5 rounded-full bg-[#DC283C] mt-1.5 shrink-0" />
                <div>
                  <p className="text-[#1E1B4B] font-medium">{t.label}</p>
                  <p className="text-[11px] text-[#94a3b8]">{t.at ? new Date(t.at).toLocaleString() : ""}{t.by ? ` · by ${t.by}` : ""}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>
      {showMaterials && (
        <MaterialIssueModal booking={booking} vendors={vendors} authAxios={authAxios}
          onClose={() => setShowMaterials(false)} onChanged={onChanged} />
      )}
    </Drawer>
  );
};

/* ---------------- shared primitives ---------------- */
const Drawer = ({ title, subtitle, onClose, children, testid, headerExtra }) => (
  <div className="fixed inset-0 z-50 flex justify-end" data-testid={testid}>
    <div className="absolute inset-0 bg-black/40" onClick={onClose} />
    <div className="relative w-full max-w-md h-full bg-white shadow-2xl overflow-y-auto animate-[slideIn_.2s_ease]">
      <div className="sticky top-0 bg-white border-b border-gray-100 p-4 flex items-center gap-3 z-10">
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-bold text-[#1E1B4B] truncate">{title}</h3>
          {subtitle && <p className="text-xs text-[#94a3b8]">{subtitle}</p>}
        </div>
        {headerExtra}
        <button onClick={onClose} className="text-[#94a3b8] hover:text-[#1E1B4B]" data-testid="drawer-close"><X size={20} /></button>
      </div>
      <div className="p-4 space-y-5">{children}</div>
    </div>
    <style>{`@keyframes slideIn{from{transform:translateX(24px);opacity:.6}to{transform:translateX(0);opacity:1}}`}</style>
  </div>
);

const Section = ({ title, children }) => (
  <div>
    <p className="text-xs font-semibold text-[#64748B] uppercase tracking-wide mb-2">{title}</p>
    {children}
  </div>
);

const Field = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-1.5">
    <Icon size={13} className="text-[#94a3b8] mt-0.5 shrink-0" />
    <div className="min-w-0"><p className="text-[10px] text-[#94a3b8] uppercase">{label}</p><p className="text-[#1E1B4B] truncate">{value || "—"}</p></div>
  </div>
);

export default OpsManagementTab;
